export interface RequestOptions {
  withAuth?: boolean;
  signal?: AbortSignal;
}

export interface PostOptions extends RequestOptions {
  keepalive?: boolean;
}

export class ApiError extends Error {
  status: number;
  detail: string;

  constructor(message: string, status: number, detail: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

export class AuthSessionExpiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthSessionExpiredError";
  }
}

export class AuthRefreshTransientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthRefreshTransientError";
  }
}

export class AuthRefreshTerminalError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthRefreshTerminalError";
  }
}

const DEFAULT_HTTP_API_BASE = "";

const normalizeBaseUrl = (
  value: string | undefined,
  fallback: string,
): string => {
  if (!value || typeof value !== "string") {
    return fallback;
  }
  return value.endsWith("/") ? value.slice(0, -1) : value;
};

const HTTP_API_BASE = normalizeBaseUrl(
  import.meta.env?.VITE_HTTP_API_URL,
  DEFAULT_HTTP_API_BASE,
);

export const API_BASE_URL = HTTP_API_BASE;
export const ACCESS_TOKEN_CHANGED_EVENT = "access-token-changed";
const ACCESS_TOKEN_STORAGE_KEY = "access_token";
const REFRESH_TOKEN_STORAGE_KEY = "refresh_token";

function dispatchAccessTokenChanged(token: string | null): void {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(ACCESS_TOKEN_CHANGED_EVENT, {
      detail: { token },
    }),
  );
}

export function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()!.split(";").shift() ?? null;
  return null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function handleResponse(res: Response): Promise<any> {
  if (!res.ok) {
    let detail = "";
    try {
      const data = await res.json();
      detail = data.detail || JSON.stringify(data);
    } catch {
      detail = res.statusText;
    }
    throw new ApiError(`API ${res.status}: ${detail}`, res.status, detail);
  }

  if (res.status === 204 || res.status === 205) {
    return null;
  }

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return res.json();
  }

  const text = await res.text();
  return text || null;
}

export function isTokenExpired(token: string): boolean {
  if (!token) return true;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return true;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");

    let jsonPayload;
    if (typeof window !== "undefined" && typeof window.atob === "function") {
      jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );
    } else if (
      typeof globalThis !== "undefined" &&
      typeof (globalThis as any).Buffer !== "undefined"
    ) {
      jsonPayload = (globalThis as any).Buffer.from(base64, "base64").toString(
        "utf8",
      );
    } else {
      return true;
    }

    const payload = JSON.parse(jsonPayload);
    if (typeof payload.exp !== "number") return true;
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

let refreshPromise: Promise<string> | null = null;
const REFRESH_TOKEN_REJECTED_STATUS = 401;
const NON_RETRYABLE_REFRESH_FAILURE_STATUSES = new Set([403, 404]);

async function refreshAccessToken() {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken || isTokenExpired(refreshToken)) {
    clearAuthTokens();
    throw new AuthSessionExpiredError(
      "Refresh token is missing or expired",
    );
  }

  refreshPromise = (async () => {
    try {
      let res: Response;
      try {
        res = await fetch(`${HTTP_API_BASE}/api/v1/token/refresh/`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ refresh: refreshToken }),
        });
      } catch {
        throw new AuthRefreshTransientError(
          "Refresh API is temporarily unavailable",
        );
      }

      if (!res.ok) {
        if (res.status === REFRESH_TOKEN_REJECTED_STATUS) {
          clearAuthTokens();
          throw new AuthSessionExpiredError(
            `Refresh session was rejected with status ${res.status}`,
          );
        }
        if (NON_RETRYABLE_REFRESH_FAILURE_STATUSES.has(res.status)) {
          throw new AuthRefreshTerminalError(
            `Refresh API failed with non-retryable status ${res.status}`,
          );
        }
        throw new AuthRefreshTransientError(
          `Refresh API temporarily failed with status ${res.status}`,
        );
      }

      let data: { access?: string; refresh?: string };
      try {
        data = await res.json();
      } catch {
        throw new AuthRefreshTransientError(
          "Refresh API returned an invalid response",
        );
      }

      if (data.access) {
        saveAccessToken(data.access);
        if (data.refresh) {
          saveRefreshToken(data.refresh);
        }
        return data.access;
      } else {
        clearAuthTokens();
        throw new AuthSessionExpiredError(
          "Access token missing in refresh response",
        );
      }
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function ensureValidAccessToken(
  forceRefresh = false,
): Promise<string> {
  const accessToken = getAccessToken();
  if (!forceRefresh && accessToken && !isTokenExpired(accessToken)) {
    return accessToken;
  }

  return refreshAccessToken();
}

async function fetchWithAuth(
  path: string,
  fetchOptions: RequestInit = {},
  options: RequestOptions = {},
) {
  const withAuth = options?.withAuth !== false;

  const makeRequest = async (token: string | null) => {
    if (options.signal?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }
    const headers: Record<string, string> = {
      ...(fetchOptions.headers as Record<string, string>),
    };
    if (withAuth && token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    return fetch(`${HTTP_API_BASE}${path}`, {
      ...fetchOptions,
      signal: options.signal ?? fetchOptions.signal,
      headers,
    });
  };

  const initialToken = getAccessToken();
  let res = await makeRequest(initialToken);

  if (res.status === 401 && withAuth) {
    const currentToken = getAccessToken();
    if (
      currentToken &&
      currentToken !== initialToken &&
      !isTokenExpired(currentToken)
    ) {
      if (options.signal?.aborted) {
        throw new DOMException("Aborted", "AbortError");
      }
      return makeRequest(currentToken);
    }

    const refreshToken = getRefreshToken();
    if (refreshToken && !isTokenExpired(refreshToken)) {
      try {
        if (options.signal?.aborted) {
          throw new DOMException("Aborted", "AbortError");
        }
        const newToken = await refreshAccessToken();
        res = await makeRequest(newToken);
      } catch (error) {
        if (
          error instanceof AuthRefreshTransientError ||
          error instanceof AuthRefreshTerminalError
        ) {
          throw error;
        }
        // Retrying fails or refresh token fails; just return the response to let caller handle
      }
    } else {
      clearAuthTokens();
    }
  }

  return res;
}

export async function apiGet<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const res = await fetchWithAuth(
    path,
    {
      method: "GET",
      credentials: "include",
    },
    options,
  );

  return handleResponse(res);
}

/**
 * Sends an authenticated GET request while leaving the response body untouched.
 * Use this for binary files and callers that need response headers.
 */
export async function apiGetResponse(
  path: string,
  options: RequestOptions = {},
): Promise<Response> {
  return fetchWithAuth(
    path,
    {
      method: "GET",
      credentials: "include",
    },
    options,
  );
}

export async function apiPost<T = unknown>(
  path: string,
  body?: unknown,
  options: PostOptions = {},
): Promise<T> {
  const csrftoken = getCookie("csrftoken");
  const bearerToken = getAccessToken();
  const withAuth = options?.withAuth !== false;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-CSRFToken": csrftoken ?? "",
    ...(withAuth && bearerToken
      ? { Authorization: `Bearer ${bearerToken}` }
      : {}),
  };

  const fetchOptions: RequestInit = {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify(body ?? {}),
  };

  if (options?.keepalive === true) {
    fetchOptions.keepalive = true;
  }

  const res = await fetchWithAuth(path, fetchOptions, options);

  return handleResponse(res);
}

export function apiPostFormDataWithProgress(
  path,
  formData,
  onProgress,
  options = {},
) {
  return new Promise((resolve, reject) => {
    const bearerToken = getAccessToken();
    const withAuth = options?.withAuth !== false;

    const runRequest = (token) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${HTTP_API_BASE}${path}`);
      xhr.withCredentials = true;

      if (withAuth && token) {
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      }

      const csrftoken = getCookie("csrftoken");
      if (csrftoken) {
        xhr.setRequestHeader("X-CSRFToken", csrftoken);
      }

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          const percentComplete = Math.round(
            (event.loaded / event.total) * 100,
          );
          onProgress(percentComplete);
        }
      };

      const rejectError = () => {
        let detail = xhr.statusText;
        try {
          const data = JSON.parse(xhr.responseText);
          detail = data.detail || JSON.stringify(data);
        } catch {
          // ignore parsing error and keep statusText
        }
        const error = new Error(`API ${xhr.status}: ${detail}`);
        error.status = xhr.status;
        error.detail = detail;
        reject(error);
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const contentType = xhr.getResponseHeader("content-type") || "";
            if (contentType.includes("application/json")) {
              resolve(JSON.parse(xhr.responseText));
            } else {
              resolve(xhr.responseText);
            }
          } catch {
            resolve(xhr.responseText);
          }
        } else if (
          xhr.status === 401 &&
          withAuth &&
          getRefreshToken() &&
          !isTokenExpired(getRefreshToken())
        ) {
          refreshAccessToken()
            .then((newToken) => {
              runRequest(newToken);
            })
            .catch(() => {
              rejectError();
            });
        } else {
          rejectError();
        }
      };

      xhr.onerror = () => reject(new Error("Network Error"));
      xhr.send(formData);
    };

    runRequest(bearerToken);
  });
}

export async function apiPatch<T = unknown>(
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const csrftoken = getCookie("csrftoken");
  const bearerToken = getAccessToken();
  const withAuth = options?.withAuth !== false;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-CSRFToken": csrftoken ?? "",
  };

  const res = await fetchWithAuth(
    path,
    {
      method: "PATCH",
      credentials: "include",
      headers,
      body: JSON.stringify(body ?? {}),
    },
    options,
  );

  return handleResponse(res);
}

export async function apiPatchFormData<T = unknown>(
  path: string,
  formData: FormData,
  options: RequestOptions = {},
): Promise<T> {
  const csrftoken = getCookie("csrftoken");
  const bearerToken = getAccessToken();
  const withAuth = options?.withAuth !== false;

  const headers: Record<string, string> = {
    "X-CSRFToken": csrftoken ?? "",
  };

  const res = await fetchWithAuth(
    path,
    {
      method: "PATCH",
      credentials: "include",
      headers,
      body: formData,
    },
    options,
  );

  return handleResponse(res);
}

export async function apiDelete<T = unknown>(
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const csrftoken = getCookie("csrftoken");
  const bearerToken = getAccessToken();
  const withAuth = options?.withAuth !== false;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-CSRFToken": csrftoken ?? "",
  };

  const res = await fetchWithAuth(
    path,
    {
      method: "DELETE",
      credentials: "include",
      headers,
      body: body ? JSON.stringify(body) : undefined,
    },
    options,
  );

  return handleResponse(res);
}

export function saveAccessToken(token: string): void {
  try {
    localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
  } catch {
    // ignore (e.g. storage disabled)
  }

  dispatchAccessTokenChanged(token);
}

export function saveRefreshToken(token: string): void {
  try {
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token);
  } catch {
    // ignore (e.g. storage disabled)
  }
}

export function saveTokenPair(
  accessToken: string,
  refreshToken: string | null = null,
): void {
  saveAccessToken(accessToken);

  if (refreshToken) {
    saveRefreshToken(refreshToken);
    return;
  }

  try {
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function getAccessToken(): string | null {
  try {
    return localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearAccessToken(): void {
  try {
    localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  } catch {
    // ignore
  }

  dispatchAccessTokenChanged(null);
}

export function clearAuthTokens(): void {
  try {
    localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  } catch {
    // ignore
  }

  dispatchAccessTokenChanged(null);
}
